import React from 'react';
import Layout from '../../../shared/components/Layout';
import {
  Target,
  DollarSign,
  Zap,
  Award,
  Rocket,
  Shield,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';

const InvestorPage: React.FC = () => {
  return (
    <Layout>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white py-20">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                跑伴 - 重新定义陪跑服务获客
              </h1>
              <p className="text-xl md:text-2xl text-orange-100 mb-8">
                基于UGC内容的低成本获客创新模式
              </p>
              <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm">
                <div className="text-center">
                  <div className="text-3xl font-bold">98倍</div>
                  <div className="text-orange-100 text-sm">ROI</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">12个月</div>
                  <div className="text-orange-100 text-sm">盈亏平衡</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">1940万</div>
                  <div className="text-orange-100 text-sm">年净利润</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 项目介绍 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                我们是谁？
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold mb-4 text-orange-600">核心业务</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>专业陪跑服务平台</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>小程序 + 短视频双引擎</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>为跑者匹配专业私兔（陪跑者）</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>创新的IP运营获客模式</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-2xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold mb-4">独家资源优势 ⭐</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="font-bold text-lg mb-2">元大都马拉松冠军俱乐部</div>
                      <div className="text-orange-100">国内最大的民间跑步俱乐部</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold">2000+</div>
                        <div className="text-orange-100 text-sm">专业会员</div>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold">全国</div>
                        <div className="text-orange-100 text-sm">覆盖范围</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 问题与解决方案 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                传统平台的困境与我们的解决方案
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                {/* 传统困境 */}
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-6 text-red-600">传统平台困境</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        ✕
                      </div>
                      <div>
                        <div className="font-bold">获客成本高</div>
                        <div className="text-gray-600 text-sm">CPA 200-500元，难以规模化</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        ✕
                      </div>
                      <div>
                        <div className="font-bold">转化率低</div>
                        <div className="text-gray-600 text-sm">仅1-3%，流量浪费严重</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        ✕
                      </div>
                      <div>
                        <div className="font-bold">盈利困难</div>
                        <div className="text-gray-600 text-sm">广告费&gt;收入，持续亏损</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 我们的方案 */}
                <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-6 text-green-600">私兔创作者计划</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        ✓
                      </div>
                      <div>
                        <div className="font-bold">激励私兔创作</div>
                        <div className="text-gray-600 text-sm">真实内容，用户信任度高</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        ✓
                      </div>
                      <div>
                        <div className="font-bold">官方IP发布</div>
                        <div className="text-gray-600 text-sm">抖音/视频号精准引流</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        ✓
                      </div>
                      <div>
                        <div className="font-bold">低成本获客</div>
                        <div className="text-gray-600 text-sm">CPA仅12元，ROI达98倍</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 商业模式 */}
        <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                商业模式与收入构成
              </h2>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">服务佣金</h3>
                  <div className="text-3xl font-bold text-orange-600 mb-2">20%</div>
                  <p className="text-gray-600 text-sm">平台抽取订单佣金</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">广告收入</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-2">15%</div>
                  <p className="text-gray-600 text-sm">品牌合作与广告</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">增值服务</h3>
                  <div className="text-3xl font-bold text-green-600 mb-2">10%</div>
                  <p className="text-gray-600 text-sm">培训与认证服务</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 财务预测 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                财务预测与投资回报
              </h2>

              <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-2xl p-8 mb-8">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">12个月</div>
                    <div className="text-orange-100">盈亏平衡</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">24个月</div>
                    <div className="text-orange-100">年收入4000万</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">1940万</div>
                    <div className="text-orange-100">年净利润</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">48.5%</div>
                    <div className="text-orange-100">净利率</div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">关键指标</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">获客成本(CPA)</span>
                      <span className="font-bold">12元</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">客单价</span>
                      <span className="font-bold">600元</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">平台佣金率</span>
                      <span className="font-bold">20%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ROI</span>
                      <span className="font-bold text-green-600">9.86倍</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">融资需求</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">融资金额</span>
                      <span className="font-bold">500万元</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">出让股份</span>
                      <span className="font-bold">15%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">估值</span>
                      <span className="font-bold">3333万元</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">资金用途</span>
                      <span className="font-bold">市场推广+团队</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 竞争优势 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                核心竞争优势
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <Shield className="w-12 h-12 text-orange-500 mb-4" />
                  <h3 className="text-xl font-bold mb-3">独家私兔资源</h3>
                  <p className="text-gray-600">
                    元大都俱乐部2000+专业会员，竞争对手难以复制
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <Zap className="w-12 h-12 text-blue-500 mb-4" />
                  <h3 className="text-xl font-bold mb-3">创新获客模式</h3>
                  <p className="text-gray-600">
                    UGC内容+IP运营，获客成本降低95%
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <Rocket className="w-12 h-12 text-green-500 mb-4" />
                  <h3 className="text-xl font-bold mb-3">快速规模化</h3>
                  <p className="text-gray-600">
                    标准化服务流程，可快速复制到全国
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              加入我们，共创跑步服务新未来
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              98倍ROI，12个月盈亏平衡，年净利润近2000万
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:contact@runbuddy.cn"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-600 rounded-lg font-bold hover:bg-gray-100 transition-colors"
              >
                联系我们
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a
                href="/portal"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold hover:bg-white hover:text-orange-600 transition-colors"
              >
                查看产品演示
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default InvestorPage;
